import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';

import { useEventing } from 'commons/eventing';
import { Flags, useFlags } from 'commons/hooks';
import { setToolboxTab } from 'craft/state/menus/actions';
import { setToolboxState, setViewportBreakpoint } from 'craft/state/viewport/actions';
import { MediaBreakpoint as Breakpoints, MediaBreakpoint, ToolboxTabs } from 'craft/types';
import { SNAPSHOT_ROOT } from 'dashboard/pages/Content/pages/Snapshots/constants';

import { ViewportDomState } from './reducer';

const breakpointIndexes = {
  [Breakpoints.SM]: 2,
  [Breakpoints.MD]: 1,
  [Breakpoints.LG]: 0,
};

export const getBreakpointIndex = (bk: MediaBreakpoint) => breakpointIndexes[bk];

export const selectViewportBreakpoint = createSelector(
  ({ viewport }) => viewport,
  ({ breakpoint }) => breakpoint,
);

export const useViewportBreakpoint = (): MediaBreakpoint => useSelector(selectViewportBreakpoint);
export const useIsUsingDefaultCssControls = (): boolean =>
  useViewportBreakpoint() === MediaBreakpoint.LG;

export const useSetViewportBreakpoint = () => {
  const dispatch = useDispatch();
  return (breakpoint: MediaBreakpoint) => dispatch(setViewportBreakpoint(breakpoint));
};

const selectViewportDom = createSelector(
  ({ viewport }) => viewport,
  ({
    viewportContainerRef,
    viewportContentRef,
    viewportWrapperRef,
    documentRef,
    scrollableBodyRef,
  }: ViewportDomState) => ({
    viewportContainerRef,
    viewportContentRef,
    viewportWrapperRef,
    documentRef,
    scrollableBodyRef,
  }),
);

export const useSelectViewportDom = () => useSelector(selectViewportDom);

export const useViewportDom = () => {
  const viewportRefs = useSelectViewportDom();

  return {
    viewportContainerEl: viewportRefs.viewportContainerRef?.current || null,
    viewportContentEl: viewportRefs.viewportContentRef?.current || null,
    viewportWrapperEl: viewportRefs.viewportWrapperRef?.current || null,
    documentEl: viewportRefs.documentRef?.current || null,
    scrollableBodyEl: viewportRefs.scrollableBodyRef?.current || null,
  };
};

const selectToolboxState = createSelector(
  ({ viewport }) => viewport,
  ({ isToolboxDocked }) => isToolboxDocked,
);

export const useSelectToolboxState = () => useSelector(selectToolboxState);

export const useToolboxState = () => {
  const { [Flags.DOCKED_TOOLBOX]: dockedToolbox } = useFlags();
  const { track } = useEventing();
  const dispatch = useDispatch();

  const storeIsDocked = useSelectToolboxState();

  const isDocked = storeIsDocked ?? !!dockedToolbox;

  /**
   * Used to minimize the toolbox when a snapshot is clicked
   * It will only minimize the toolbox if it's visible and the click comes from a snapshot
   *
   * @returns {boolean} true if the toolbox was minimized, false otherwise
   */
  const minimizeToolboxOnSnapshotClick = useCallback(() => {
    if (!dockedToolbox) {
      // if flag is turned off, don't do anything
      return false;
    }

    if (!isDocked && document.getElementById(SNAPSHOT_ROOT)) {
      // if click comes from snapshot and the toolbox is visible, minimize it
      dispatch(setToolboxState(true));
      return true;
    }

    return false;
  }, [dockedToolbox, isDocked]);

  /**
   * Used to set the docked state of the toolbox, if it's different to the current one
   *
   * @param {boolean} [newIsDockedState] - the new state to set, if not provided, it will toggle the current one
   * @returns {boolean} true if the state was changed, false otherwise
   */
  const setDockedState = useCallback(
    (newIsDockedState?: boolean) => {
      if (!dockedToolbox) {
        // if flag is turned off, don't do anything
        return false;
      }

      if (typeof newIsDockedState !== 'undefined') {
        if (newIsDockedState !== isDocked) {
          dispatch(setToolboxState(newIsDockedState));
          return true;
        }
        return false;
      }

      // if no new state is provided, toggle the current one
      dispatch(setToolboxState(!isDocked));
      return true;
    },
    [isDocked, dockedToolbox],
  );

  /**
   * Used to programmatically open the toolbox, usually when snapshot placements need action
   */
  const forceOpenToolbox = useCallback(() => {
    if (!dockedToolbox) {
      // if flag is turned off, don't do anything
      return;
    }

    if (!isDocked) {
      // if the toolbox is already open, don't do anything
      return;
    }

    dispatch(setToolboxState(false));
  }, [isDocked, track]);

  return {
    isToolboxDocked: isDocked,
    setDockedState,
    forceOpenToolbox,
    minimizeToolboxOnSnapshotClick,
    setTab: (tab: ToolboxTabs) => dispatch(setToolboxTab(tab)),
  };
};
